import { baseTheme } from '@src/theme';
import { EInputIntents, ETypographyIntents } from '@src/typings';
import { selectVariants } from '@src/utilities/selectVariants';
import { Tag } from 'antd';
import { debounce } from 'lodash';
import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { HiChevronDown } from 'react-icons/hi';
import {
    HiMagnifyingGlass,
    HiOutlineQuestionMarkCircle,
} from 'react-icons/hi2';
import { twMerge } from 'tailwind-merge';
import { Input } from '../Input';
import { useTheme, withTheme } from '../ThemeProvider';
import { Typography } from '../Typography';
import { SelectOverrideStyles, StyledSelect } from './styles';
import { SelectCustomProps } from './types';

function SelectComponentBase<T extends string | number>(
    {
        intent,
        size,
        bordered = true,
        className,
        options,
        label,
        isRequired,
        help,
        error,
        optionRender,
        tagRender,
        searchInput,
        searchPlaceholder,
        theme: propTheme,
        ...props
    }: SelectCustomProps<T>,
    ref: ForwardedRef<any>
) {
    const [searchTerm, setSearchTerm] = useState('');
    const { theme: contextTheme } = useTheme();
    const themeToUse = propTheme?.colors
        ? propTheme
        : contextTheme?.colors
          ? contextTheme
          : baseTheme;

    const handleSearchDebounced = useCallback(
        debounce((value: string) => {
            setSearchTerm(value.toLowerCase());
        }, 300),
        []
    );

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleSearchDebounced(e.target.value);
    };

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options?.filter((opt) =>
            opt.label.toLowerCase().includes(searchTerm)
        );
    }, [searchTerm]);

    return (
        <div className="flex flex-col w-full">
            {label && (
                <div className="flex w-full items-center justify-between mb-2.5">
                    <Typography
                        intent={ETypographyIntents.Regular}
                        className="flex items-center justify-start gap-x-1"
                        style={{ color: themeToUse.colors.secondary.main.text }}
                    >
                        <span>{label}</span>
                        <span
                            className="text-lg"
                            style={{ color: themeToUse.colors.error.main.text }}
                        >
                            {isRequired ? '*' : ''}
                        </span>
                    </Typography>
                    {help ? (
                        <div
                            className="p-1 rounded-lg"
                            style={{
                                backgroundColor:
                                    themeToUse.colors.secondary.light.surfaceBg,
                            }}
                        >
                            <HiOutlineQuestionMarkCircle
                                className="w-4 h-4"
                                width={16}
                                height={16}
                                style={{
                                    color: themeToUse.colors.info.main.icon,
                                }}
                            />
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            )}
            <SelectOverrideStyles />
            <StyledSelect
                ref={ref}
                className={twMerge(
                    selectVariants({
                        className,
                        intent,
                        size,
                        bordered,
                        error: !!error,
                    })
                )}
                bordered={bordered}
                error={!!error}
                dropdownRender={(menu) => (
                    <>
                        {searchInput && (
                            <Input
                                className="mb-2 max-h-12"
                                intent={EInputIntents.outlined}
                                placeholder={searchPlaceholder}
                                onChange={onInputChange}
                                suffix={<HiMagnifyingGlass />}
                                theme={themeToUse}
                            />
                        )}
                        {menu}
                    </>
                )}
                options={filteredOptions}
                suffixIcon={<HiChevronDown size={16} />}
                optionRender={
                    optionRender ??
                    ((option) => (
                        <div className="flex items-center">
                            {option.data.emoji && (
                                <span
                                    role="img"
                                    aria-label={String(option.data.label)}
                                    className="mr-2"
                                >
                                    {option.data.emoji}
                                </span>
                            )}
                            <p
                                className="mr-2"
                                style={{
                                    color: themeToUse.colors.secondary.main
                                        .text,
                                }}
                            >
                                {option.data.label}
                            </p>
                        </div>
                    ))
                }
                tagRender={
                    tagRender ??
                    ((props) => (
                        <Tag
                            closable={props.closable}
                            onClose={props.onClose}
                            style={{
                                marginInlineEnd: 4,
                                backgroundColor:
                                    themeToUse.colors.secondary.light.surfaceBg,
                                color: themeToUse.colors.secondary.main.text,
                            }}
                        >
                            <Typography intent={ETypographyIntents.Regular}>
                                {props.label}
                            </Typography>
                        </Tag>
                    ))
                }
                {...props}
            />
            {error && (
                <Typography
                    intent={ETypographyIntents.Badge}
                    style={{ color: themeToUse.colors.error.main.text }}
                >
                    {error}
                </Typography>
            )}
        </div>
    );
}

const SelectComponent = forwardRef(SelectComponentBase) as (<
    T extends string | number,
>(
    props: SelectCustomProps<T> & { ref?: ForwardedRef<any> }
) => JSX.Element) & { displayName?: string };

SelectComponent.displayName = 'SelectComponent';

export const Select = withTheme(SelectComponent);
